import {
  Component,
  DestroyRef,
  ElementRef,
  inject,
  signal,
  viewChild,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { NavigationEnd, NavigationStart, Router } from '@angular/router';
import { filter, finalize, map } from 'rxjs';
import { NotificationDto, NotificationCenterService } from '../../../api/gen';
import { NotificationSettingsDataService } from '../../services/data/notification-settings-data.service';
import { NotificationCenterMessagingService } from '../../services/signaling/messaging/notification-center-messaging.service';

@Component({
  selector: 'app-notification-center',
  standalone: false,
  templateUrl: './notification-center.component.html',
  styleUrls: ['./notification-center.component.scss'],
  host: {
    '(document:click)': 'handleOutsideClick($event)',
    '(document:touchstart)': 'handleOutsideClick($event)',
    '(document:keydown.escape)': 'close(true)',
  },
})
export class NotificationCenterComponent {
  private readonly notificationCenterService = inject(
    NotificationCenterService,
  );
  private readonly signaling = inject(NotificationCenterMessagingService);
  private readonly destroyRef = inject(DestroyRef);
  private readonly router = inject(Router);
  private readonly host = inject<ElementRef<HTMLElement>>(ElementRef);
  private readonly toggle =
    viewChild<ElementRef<HTMLButtonElement>>('openCloseButton');
  protected readonly preferences = inject(NotificationSettingsDataService);
  protected readonly notifications = signal<NotificationDto[]>([]);
  protected readonly loading = signal(true);
  protected readonly failed = signal(false);
  protected readonly open = signal(false);
  private requestedOpen: ReturnType<typeof setTimeout> | undefined;
  protected readonly settingsLabel = $localize`:@@notificationSettingsTitle:Benachrichtigungseinstellungen`;

  constructor() {
    this.destroyRef.onDestroy(() => clearTimeout(this.requestedOpen));
    this.preferences.load();
    this.signaling.notificationCreated
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => this.refreshNotifications());
    this.router.events
      .pipe(
        filter((event) => event instanceof NavigationStart),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe(() => {
        clearTimeout(this.requestedOpen);
        this.close();
      });
    this.router.events
      .pipe(
        filter((event) => event instanceof NavigationEnd),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe(() => this.openFromRoute());
    this.openFromRoute();
    this.refreshNotifications();
  }

  private openFromRoute(): void {
    if (this.router.parseUrl(this.router.url).fragment !== 'notifications')
      return;
    // Finish the originating link click before the document outside-click handler runs.
    this.requestedOpen = setTimeout(() => {
      if (
        !this.destroyRef.destroyed &&
        this.router.parseUrl(this.router.url).fragment === 'notifications'
      )
        this.open.set(true);
    });
  }

  protected refreshNotifications(): void {
    this.loading.set(true);
    this.failed.set(false);
    this.notificationCenterService
      .notificationCenterV2NotificationMonthGet()
      .pipe(
        map((result) => result.filter((item) => !item.readAt)),
        finalize(() => this.loading.set(false)),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe({
        next: (notifications) => this.notifications.set(notifications),
        error: () => this.failed.set(true),
      });
  }

  protected bellLabel(): string {
    return $localize`:@@notificationBellLabel:Benachrichtigungen: ${this.notifications().length}`;
  }

  protected handleOutsideClick(event: MouseEvent | TouchEvent): void {
    if (
      event.target instanceof Node &&
      !this.host.nativeElement.contains(event.target)
    )
      this.close();
  }

  protected toggleCenter(): void {
    this.open.update((value) => !value);
  }
  protected close(restoreFocus = false): void {
    if (!this.open()) return;
    this.open.set(false);
    if (restoreFocus) this.toggle()?.nativeElement.focus();
  }
  protected openSettings(): void {
    this.close();
    void this.router
      .navigate(['/profile'], { fragment: 'notification-settings' })
      .then(() => {
        const settings = document.getElementById('notification-settings');
        settings?.scrollIntoView({ behavior: 'smooth', block: 'center' });
        settings
          ?.querySelector<HTMLElement>('#notification-settings-title')
          ?.focus({ preventScroll: true });
      });
  }
  protected onNotificationRead(notification: NotificationDto): void {
    this.notifications.update((current) =>
      current.filter((item) => item.id !== notification.id),
    );
  }
}
